import { formatCentsToUsd } from '@/utils/currency';
import { formatNumber } from '@/utils/number';
import { FLEX_CREDITS_CONFIG } from './credits-purchase-config';

export const ADD_FUNDS_PRESETS_DOLLARS = [10, 25, 50, 100] as const;
const CUSTOM_AMOUNT_MIN_DOLLARS = FLEX_CREDITS_CONFIG.minQuantity;
const CUSTOM_AMOUNT_MAX_DOLLARS = FLEX_CREDITS_CONFIG.maxQuantity;
const wholeDollars = (dollars: number) => formatCentsToUsd(dollars * 100, 0);
export const CUSTOM_AMOUNT_RANGE = `${wholeDollars(CUSTOM_AMOUNT_MIN_DOLLARS)} to ${wholeDollars(CUSTOM_AMOUNT_MAX_DOLLARS)}`;

export const AUTO_TOP_UP_MIN_TRIGGER_DOLLARS = 5;
export const AUTO_TOP_UP_MIN_REPLENISH_DOLLARS = 10;
export const AUTO_TOP_UP_MAX_REPLENISH_DOLLARS = 100;

export type AutoTopUpSettings = {
  isEnabled: boolean;
  triggerDollars: number | null;
  replenishDollars: number | null;
  isMonthlyLimitEnabled: boolean;
  monthlyLimitDollars: number | null;
};

export const DEFAULT_AUTO_TOP_UP_SETTINGS: AutoTopUpSettings = {
  isEnabled: false,
  triggerDollars: 10,
  replenishDollars: 50,
  isMonthlyLimitEnabled: false,
  monthlyLimitDollars: 200,
};

export type AutoTopUpErrors = {
  trigger?: string;
  replenish?: string;
  monthlyLimit?: string;
};

export type AmountSelection = { kind: 'preset'; dollars: number } | { kind: 'custom'; dollars: number | null };

export type AddFundsFunding =
  | { kind: 'send'; name: string; needCents: number; emails: number }
  | { kind: 'automation'; name: string; needCents: number }
  | { kind: 'import'; name: string; needCents: number; newProfiles: number };

export function fundingTitle(funding: AddFundsFunding): string {
  switch (funding.kind) {
    case 'send':
      return `Add funds to send ${funding.name}`;
    case 'automation':
      return `Add funds to start ${funding.name}`;
    case 'import':
      return `Add funds to import ${funding.name}`;
    default: {
      const _exhaustive: never = funding;
      return String(_exhaustive);
    }
  }
}

export function fundingTag(funding: AddFundsFunding): string | null {
  switch (funding.kind) {
    case 'send':
      return 'Covers this send';
    case 'import':
      return 'Covers this import';
    case 'automation':
      return null;
    default: {
      const _exhaustive: never = funding;
      return String(_exhaustive);
    }
  }
}

export function coveringAmountDollars(needCents: number): number {
  const preset = ADD_FUNDS_PRESETS_DOLLARS.find((dollars) => dollars * 100 >= needCents);
  if (preset != null) return preset;
  return Math.min(CUSTOM_AMOUNT_MAX_DOLLARS, Math.ceil(needCents / 1000) * 10);
}

function selectionFor(dollars: number): AmountSelection {
  return ADD_FUNDS_PRESETS_DOLLARS.some((preset) => preset === dollars)
    ? { kind: 'preset', dollars }
    : { kind: 'custom', dollars };
}

export function initialAmountSelection(initialDollars: number | undefined, funding?: AddFundsFunding): AmountSelection {
  if (initialDollars != null) return selectionFor(initialDollars);
  if (funding != null) return selectionFor(coveringAmountDollars(funding.needCents));
  return selectionFor(ADD_FUNDS_PRESETS_DOLLARS[0]);
}

export function approximateEmails(count: number): string {
  if (count >= 1000) return `${formatNumber(Math.round(count / 1000))}K`;
  return formatNumber(Math.round(count));
}

export type FundingOutcome =
  | { kind: 'covers'; extraEmails: string | null }
  | { kind: 'above-estimate' }
  | { kind: 'short'; pickDollars: number };

export type FundingSummary = {
  subject: string;
  verb: string;
  needCents: number;
  what: string;
  outcome: FundingOutcome | null;
};

function fundingSubject(funding: AddFundsFunding): Pick<FundingSummary, 'subject' | 'verb' | 'what'> {
  switch (funding.kind) {
    case 'send':
      return { subject: 'This send', verb: 'needs', what: `${formatNumber(funding.emails)} emails` };
    case 'automation':
      return { subject: 'This automation', verb: 'will likely cost', what: 'an estimate for its first 30 days' };
    case 'import':
      return { subject: 'This import', verb: 'needs', what: `${formatNumber(funding.newProfiles)} new profiles` };
    default: {
      const _exhaustive: never = funding;
      return _exhaustive;
    }
  }
}

function extraEmails(funding: AddFundsFunding, amountCents: number): string | null {
  if (funding.kind !== 'send' || funding.emails <= 0 || funding.needCents <= 0) return null;
  const centsPerEmail = funding.needCents / funding.emails;
  return approximateEmails((amountCents - funding.needCents) / centsPerEmail);
}

function fundingOutcome(funding: AddFundsFunding, amountCents: number): FundingOutcome | null {
  if (amountCents <= 0) return null;
  if (amountCents < funding.needCents) {
    return { kind: 'short', pickDollars: coveringAmountDollars(funding.needCents) };
  }
  if (funding.kind === 'automation') return { kind: 'above-estimate' };
  return { kind: 'covers', extraEmails: extraEmails(funding, amountCents) };
}

export function fundingSummary(funding: AddFundsFunding, amountCents: number): FundingSummary {
  return {
    ...fundingSubject(funding),
    needCents: funding.needCents,
    outcome: fundingOutcome(funding, amountCents),
  };
}

export function customAmountError(dollars: number | null): string | undefined {
  if (
    dollars == null ||
    !Number.isInteger(dollars) ||
    dollars < CUSTOM_AMOUNT_MIN_DOLLARS ||
    dollars > CUSTOM_AMOUNT_MAX_DOLLARS
  ) {
    return `Enter a whole amount from ${CUSTOM_AMOUNT_RANGE}.`;
  }
  return undefined;
}

export function refillCents(settings: AutoTopUpSettings): number {
  return ((settings.replenishDollars ?? 0) - (settings.triggerDollars ?? 0)) * 100;
}

export function autoTopUpErrors(settings: AutoTopUpSettings): AutoTopUpErrors {
  if (!settings.isEnabled) return {};

  const { triggerDollars, replenishDollars } = settings;
  const errors: AutoTopUpErrors = {};

  if (triggerDollars == null || triggerDollars < AUTO_TOP_UP_MIN_TRIGGER_DOLLARS) {
    errors.trigger = 'Set the trigger to at least $5.';
  }

  if (
    replenishDollars == null ||
    replenishDollars < AUTO_TOP_UP_MIN_REPLENISH_DOLLARS ||
    replenishDollars > AUTO_TOP_UP_MAX_REPLENISH_DOLLARS
  ) {
    errors.replenish = 'Replenish to an amount between $10 and $100.';
  } else if (triggerDollars != null && replenishDollars <= triggerDollars) {
    errors.replenish = 'The replenish amount has to be higher than the trigger.';
  }

  const refill = refillCents(settings);
  if (
    settings.isMonthlyLimitEnabled &&
    errors.trigger == null &&
    errors.replenish == null &&
    (settings.monthlyLimitDollars ?? 0) * 100 < refill
  ) {
    errors.monthlyLimit = `Set the limit to at least ${formatCentsToUsd(refill, 0)}, which covers one refill.`;
  }

  return errors;
}

export function hasAutoTopUpErrors(errors: AutoTopUpErrors): boolean {
  return errors.trigger != null || errors.replenish != null || errors.monthlyLimit != null;
}

export function immediateTopUpChargeCents(balanceCents: number, settings: AutoTopUpSettings): number {
  const { triggerDollars, replenishDollars } = settings;
  if (!settings.isEnabled || triggerDollars == null || replenishDollars == null) return 0;
  if (balanceCents > triggerDollars * 100) return 0;
  return Math.max(0, replenishDollars * 100 - balanceCents);
}
